import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    toggleArticleLike,
    toggleArticleDislike,
    fetchReactionStatus,
} from '../../slices/reactionsSlice.mjs';
import AnimatedNumber from './AnimatedNumber.mjs';
import { shallowEqual } from 'react-redux';

const LikeDislikeButtons = ({
    articleId,
    variant = 'card',
    showCounts = true,
    disabled = false,
    className = '',
}) => {
    const dispatch = useDispatch();
    const { user, isAuthenticated } = useSelector((state) => state.auth);

    // Combine optimistic and actual data in one selector with deep comparison
    const reactionData = useSelector((state) => {
        const optimisticData = state.reactions?.optimisticUpdates?.[articleId];
        const actualData = state.reactions?.byArticleId?.[articleId];
        const data = optimisticData || actualData || { likes: 0, dislikes: 0, userReaction: null };
        return {
            likes: data.likes || 0,
            dislikes: data.dislikes || 0,
            userReaction: data.userReaction || null,
            lastUpdated: data.lastUpdated || null,
            hasActualData: !!actualData,
        };
    }, shallowEqual); // Use shallowEqual to ensure re-render on any change

    const isToggling = useSelector(
        (state) => state.reactions?.loading?.toggle?.[articleId] || false
    );

    const toggleError = useSelector(
        (state) => state.reactions?.errors?.toggle?.[articleId] || null
    );

    const hasFetched = useSelector((state) => state.reactions?.fetched?.[articleId] || false);

    const isCurrentlyLoading = useSelector(
        (state) => state.reactions?.loading?.fetch?.[articleId] || false
    );

    const { likes, dislikes, userReaction, hasActualData, lastUpdated } = reactionData;

    // Fetch reaction status on mount if no data or data is stale
    // useEffect(() => {
    //     if (!articleId || hasFetched || isCurrentlyLoading) return;

    //     const isDataStale = lastUpdated && (Date.now() - lastUpdated) > (60 * 60 * 1000);

    //     if (!hasActualData || !lastUpdated || isDataStale) {
    //         console.log('Fetching reaction status for:', articleId);
    //         dispatch(fetchReactionStatus({ articleId }));
    //     }
    // }, [articleId, dispatch, hasFetched, isCurrentlyLoading, hasActualData, lastUpdated]);

    // Clear errors after a delay
    useEffect(() => {
        if (toggleError) {
            const timer = setTimeout(() => {
                // dispatch(clearArticleError({ articleId }));
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [toggleError, dispatch, articleId]);

    const handleLike = (event) => {
        event.stopPropagation();
        event.preventDefault();
        if (!isAuthenticated) {
            alert('Please log in to like articles');
            return;
        }
        if (disabled || isToggling) return;
        console.log('Dispatching toggleArticleLike for:', articleId);
        dispatch(toggleArticleLike({ articleId }));
    };

    const handleDislike = (event) => {
        event.stopPropagation();
        event.preventDefault();
        if (!isAuthenticated) {
            alert('Please log in to react to articles');
            return;
        }
        if (disabled || isToggling) return;
        console.log('Dispatching toggleArticleDislike for:', articleId);
        dispatch(toggleArticleDislike({ articleId }));
    };

    const isLikeActive = userReaction === 'like';
    const isDislikeActive = userReaction === 'dislike';
    const isButtonDisabled = disabled || isToggling || !isAuthenticated;

    return (
        <div className={`like-dislike-buttons ${variant} ${className}`}>
            <button
                onClick={handleLike}
                data-reaction-button
                disabled={isButtonDisabled}
                className={`reaction-button like-button ${isLikeActive ? 'active' : ''}`}
                title={!isAuthenticated ? 'Log in to like articles' : 'Like this article'}
                aria-label={`Like article${showCounts ? ` (${likes} likes)` : ''}`}
            >
                <svg
                    className="reaction-icon"
                    fill={isLikeActive ? 'currentColor' : 'none'}
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                </svg>
                {showCounts && (
                    <AnimatedNumber value={likes} variant={variant} aria-label={`${likes} likes`} />
                )}
            </button>

            <button
                onClick={handleDislike}
                data-reaction-button
                disabled={isButtonDisabled}
                className={`reaction-button dislike-button ${isDislikeActive ? 'active' : ''}`}
                title={!isAuthenticated ? 'Log in to react to articles' : 'Dislike this article'}
                aria-label={`Dislike article${showCounts ? ` (${dislikes} dislikes)` : ''}`}
            >
                <svg
                    className="reaction-icon"
                    fill={isDislikeActive ? 'currentColor' : 'none'}
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                    />
                </svg>
                {showCounts && (
                    <AnimatedNumber
                        value={dislikes}
                        variant={variant}
                        aria-label={`${dislikes} dislikes`}
                    />
                )}
            </button>

            {toggleError && (
                <div className="reaction-error" role="alert">
                    <span className="sr-only">Error:</span>
                    {toggleError}
                </div>
            )}

            {isToggling && (
                <div className="reaction-loading" aria-live="polite">
                    <span className="sr-only">Updating reaction...</span>
                    <div className="loading-spinner"></div>
                </div>
            )}
        </div>
    );
};

export default LikeDislikeButtons;
